import { getUnprocessableErrors } from '@10xlabs/utils'
import { useMutation } from '@apollo/client'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { DriverPersonalInformationCard } from 'features/Drivers/components/DriverPersonalInformationFormCard'
import { driverStep1Schema } from 'features/Drivers/components/NewDrivers/Step1/schema'
import { INewDriverStep1 } from 'features/Drivers/types'
import { mapDriverToStep1Values } from 'features/Drivers/utils'
import { Formik, FormikHelpers } from 'formik'
import { CHANGE_TDRIVER_DETAILS, CREATE_DRIVER } from 'features/Drivers/mutations-legacy'
import { GTChangeUserAccountDetailsInput, GTTDriver, GTUserAccountGenderInput } from 'gql/types.generated'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { CHANGE_USER_ACCOUNT_DETAILS } from 'features/BusinessCustomer/mutations-legacy'
import { getDifference } from 'utils/object'
import { isEmpty } from 'lodash'
import { isAccountsConflictError } from 'utils/error'
import { DEFAULT_COUNTRY_CODE } from 'components/Formik/InputPhone/country-codes-list'

interface INewDriverStep1Props {
  driver?: GTTDriver
  onSubmit: (driverId?: string) => void
  onCancel: () => void
  isEdit?: boolean
}

export const NewDriverStep1 = ({ driver, onSubmit, onCancel, isEdit }: INewDriverStep1Props): ReactElement => {
  const intl = useIntl()
  const [createDriver, { error: createDriverError }] = useMutation(CREATE_DRIVER, {
    onError: (): void => undefined,
    onCompleted: (data): void => onSubmit(data.createDriver?.id)
  })
  const [changeTDriverDetails, { error: changeDriverDetailsError }] = useMutation(CHANGE_TDRIVER_DETAILS, {
    onError: (): void => undefined,
    onCompleted: (): void => onSubmit()
  })
  const [changeUserAccountDetails, { error: changeUserAccountDetailsError }] = useMutation(
    CHANGE_USER_ACCOUNT_DETAILS,
    {
      onError: (): void => undefined,
      onCompleted: (): void => onSubmit()
    }
  )

  const handleSubmit = async (
    values: INewDriverStep1,
    { setSubmitting }: FormikHelpers<INewDriverStep1>
  ): Promise<void> => {
    if (isEdit) {
      const oldDriverDetails = {
        partnerID: driver?.partner.id,
        stateCode: driver?.stateCode || undefined
      }
      const newDriverDetails = {
        partnerID: values.partner.id,
        stateCode: values.state || undefined
      }
      const driverDetailsPayload = getDifference(newDriverDetails, oldDriverDetails)
      if (!isEmpty(driverDetailsPayload)) {
        await changeTDriverDetails({
          variables: {
            id: driver?.id,
            input: newDriverDetails
          }
        })
      }

      const oldUserAccountDetails: GTChangeUserAccountDetailsInput = {
        id: driver?.userAccount?.id,
        email: driver?.userAccount?.email || '',
        firstName: driver?.userAccount?.firstName || '',
        lastName: driver?.userAccount?.lastName || '',
        phoneNumber: driver?.userAccount?.phoneNumber?.number
          ? {
              number: driver?.userAccount?.phoneNumber?.number,
              countryCode: driver?.userAccount?.phoneNumber?.countryCode
            }
          : null,
        gender: (driver?.userAccount?.gender || undefined) as GTUserAccountGenderInput | undefined
      }

      const newUserAccountDetails: GTChangeUserAccountDetailsInput = {
        id: driver?.userAccount?.id,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phone
          ? {
              number: values.phone,
              countryCode: driver?.userAccount?.phoneNumber?.countryCode || DEFAULT_COUNTRY_CODE
            }
          : null,
        gender: (values.gender || undefined) as GTUserAccountGenderInput | undefined
      }
      const userAccountDetailsPayload = getDifference(newUserAccountDetails, oldUserAccountDetails)
      if (!isEmpty(userAccountDetailsPayload)) {
        const isNameModified = userAccountDetailsPayload.firstName || userAccountDetailsPayload.lastName
        userAccountDetailsPayload.firstName = isNameModified ? values.firstName : undefined
        userAccountDetailsPayload.lastName = isNameModified ? values.lastName : undefined

        await changeUserAccountDetails({
          variables: {
            input: {
              id: driver?.userAccount?.id,
              ...userAccountDetailsPayload,
              phoneNumber: userAccountDetailsPayload.phoneNumber
                ? {
                    number: newUserAccountDetails.phoneNumber?.number,
                    countryCode: newUserAccountDetails.phoneNumber?.countryCode
                  }
                : undefined
            }
          }
        })
      }
    } else {
      await createDriver({
        variables: {
          input: {
            partnerID: values.partner.id,
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phone || undefined,
            email: values.email,
            stateCode: values.state || undefined,
            gender: values.gender || undefined
          }
        }
      })
    }
    setSubmitting(false)
  }
  const initialValues = mapDriverToStep1Values(driver)

  return (
    <Formik initialValues={initialValues} validationSchema={driverStep1Schema(intl)} onSubmit={handleSubmit}>
      {({ handleSubmit, dirty, isSubmitting }): ReactElement => {
        const isCreating = !isEdit
        const isEditing = !isCreating && dirty

        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <DriverPersonalInformationCard />
            {createDriverError && (
              <FormLayout.Actions>
                {isAccountsConflictError(createDriverError) ? (
                  <ErrorMessage>
                    <FormattedMessage
                      id="drivers.new.data.accountsConflict"
                      defaultMessage="The email and phone number are already used in other user accounts"
                    />
                  </ErrorMessage>
                ) : (
                  <ErrorMessage errors={getUnprocessableErrors(createDriverError)}>
                    <FormattedMessage
                      id="drivers.new.data.error"
                      defaultMessage="There was an error while creating the driver"
                    />
                  </ErrorMessage>
                )}
              </FormLayout.Actions>
            )}
            {(changeDriverDetailsError || changeUserAccountDetailsError) && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeDriverDetailsError)}>
                  <FormattedMessage
                    id="drivers.edit.data.error"
                    defaultMessage="There was an error while updating the driver"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={onCancel} disabled={isSubmitting}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" loading={isSubmitting}>
                {isCreating && (
                  <FormattedMessage id="partners.new.step1.createAndContinue" defaultMessage="Create & Continue" />
                )}
                {isEditing && (
                  <FormattedMessage id="partners.new.step1.saveAndContinue" defaultMessage="Save & Continue" />
                )}
                {!isCreating && !isEditing && (
                  <FormattedMessage id="partners.new.step1.Continue" defaultMessage="Next" />
                )}
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
